import { useQuery, useQueryClient } from '@tanstack/react-query';
import { createContext, useCallback, useContext, useMemo, type JSX, type ReactNode } from 'react';
import { setBusinessId } from '../api/client';
import { businessesApi } from '../api/endpoints';
import { queryKeys } from '../api/queryKeys';
import type { Business } from '../api/types';
import { useAuth } from '../auth/AuthContext';

/**
 * The PLATFORM_ADMIN's business switcher (STEP-12). The selection lives in the
 * axios layer (X-Business-Id on every call, persisted per device); switching
 * wipes every cached query and the store-context selection — nothing from the
 * previous business may survive on screen — then re-reads /auth/me so the
 * effective role/business come from the server. For everyone else this is
 * inert: their business is fixed by their account.
 */

const STORE_CONTEXT_KEY = 'ribaAcc.storeContext';

interface BusinessContextValue {
  /** Non-archived businesses the platform admin may enter ([] for others). */
  businesses: Business[];
  businessesLoading: boolean;
  setBusiness: (id: string | null) => Promise<void>;
}

const BusinessContext = createContext<BusinessContextValue | null>(null);

export function BusinessContextProvider({ children }: { children: ReactNode }): JSX.Element {
  const { isPlatformAdmin, refreshMe } = useAuth();
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: queryKeys.businesses,
    queryFn: businessesApi.list,
    enabled: isPlatformAdmin,
    staleTime: 60_000,
  });

  const setBusiness = useCallback(
    async (id: string | null): Promise<void> => {
      if (!isPlatformAdmin) return;
      setBusinessId(id);
      localStorage.removeItem(STORE_CONTEXT_KEY);
      queryClient.clear();
      // Entering/leaving a business flips the shell mode, which REBUILDS the
      // data router from window.location (App.tsx). Put the destination in the
      // URL first so the new router starts there; callers may still navigate()
      // afterwards for the same-mode case (switching between two businesses).
      window.history.replaceState(null, '', id ? '/' : '/businesses');
      await refreshMe();
    },
    [isPlatformAdmin, queryClient, refreshMe],
  );

  const value = useMemo<BusinessContextValue>(
    () => ({
      businesses: (query.data ?? []).filter((b) => !b.isArchived),
      businessesLoading: query.isLoading,
      setBusiness,
    }),
    [query.data, query.isLoading, setBusiness],
  );
  return <BusinessContext.Provider value={value}>{children}</BusinessContext.Provider>;
}

export function useBusinessContext(): BusinessContextValue {
  const ctx = useContext(BusinessContext);
  if (!ctx) throw new Error('useBusinessContext outside BusinessContextProvider');
  return ctx;
}
